import React from "react";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import {
  FieldPair,
  StyledEditField,
  StyledFieldLabel,
  StyledReadOnlyField,
  createStyledReadOnlyFieldProps,
} from "../translation_modal/styles";
import { createSpanishFieldProps } from "../translation_modal/utils";

export const DiscountCardExclusionTranslationFields = ({
  descriptionLabel,
  exclusions,
  getTranslation,
  isReadOnly,
  onTranslationChange,
  testIdPrefix = "",
  sectionTitle,
  translateOr,
  variantId,
}) => {
  if (!exclusions.length) {
    return null;
  }

  const buildTestId = (suffix) =>
    testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix;

  const getFieldValue = (item, field) =>
    variantId ? getTranslation(variantId, item.id, field) : getTranslation(item.id, field);

  const handleFieldChange = (item, field, value) => {
    if (variantId) {
      onTranslationChange(variantId, item.id, field, value);
      return;
    }

    onTranslationChange(item.id, field, value);
  };

  return (
    <Box sx={{ marginBottom: "16px", marginTop: "24px" }}>
      <Typography
        sx={{
          color: "#323232",
          fontSize: "18px",
          fontWeight: 600,
          lineHeight: "24px",
          marginBottom: "16px",
        }}
      >
        {sectionTitle}
      </Typography>

      {exclusions.map((item, index) => (
        <Box
          key={item.id || `${item.category}-${index}`}
          sx={{
            backgroundColor: "#f9f9f9",
            borderRadius: "8px",
            marginBottom: "16px",
            padding: "16px",
          }}
        >
          <Typography
            sx={{ color: "#666", fontSize: "14px", fontWeight: 600, mb: 1.5 }}
          >
            {`${translateOr("discount-cards.create.form.exclusions-item", "Exclusion")} ${index + 1}`}
          </Typography>

          <Box sx={{ marginBottom: "12px" }}>
            <StyledFieldLabel>
              {translateOr(
                "discount-cards.create.form.exclusion-category",
                "Category",
              )}
            </StyledFieldLabel>
            <FieldPair>
              <StyledReadOnlyField
                data-testid={buildTestId(
                  `exclusion-category-readonly-${index}`,
                )}
                disabled
                fullWidth
                label="English"
                size="small"
                value={item.category}
                variant="outlined"
                {...createStyledReadOnlyFieldProps()}
              />
              <StyledEditField
                data-testid={buildTestId(`exclusion-category-${index}`)}
                fullWidth
                label="Español"
                onChange={(event) =>
                  handleFieldChange(item, "spanish_category", event.target.value)
                }
                size="small"
                value={getFieldValue(item, "spanish_category")}
                variant="outlined"
                {...createSpanishFieldProps(item.category, isReadOnly)}
              />
            </FieldPair>
          </Box>

          <Box>
            <StyledFieldLabel>{descriptionLabel}</StyledFieldLabel>
            <FieldPair>
              <StyledReadOnlyField
                data-testid={buildTestId(
                  `exclusion-description-readonly-${index}`,
                )}
                disabled
                fullWidth
                label="English"
                multiline
                rows={3}
                size="small"
                value={item.description}
                variant="outlined"
                {...createStyledReadOnlyFieldProps()}
              />
              <StyledEditField
                data-testid={buildTestId(`exclusion-description-${index}`)}
                fullWidth
                label="Español"
                multiline
                onChange={(event) =>
                  handleFieldChange(item, "spanish_description", event.target.value)
                }
                rows={3}
                size="small"
                value={getFieldValue(item, "spanish_description")}
                variant="outlined"
                {...createSpanishFieldProps(item.description, isReadOnly)}
              />
            </FieldPair>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

DiscountCardExclusionTranslationFields.propTypes = {
  descriptionLabel: PropTypes.string.isRequired,
  exclusions: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      description: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  getTranslation: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  onTranslationChange: PropTypes.func.isRequired,
  sectionTitle: PropTypes.string.isRequired,
  testIdPrefix: PropTypes.string,
  translateOr: PropTypes.func.isRequired,
  variantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
